import { DeviationIcon } from '@app/src/pages/Questionnaire/DeviationIcon'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import { calculateDeviation, getPreviousAnswerSummary } from '@app/src/utils/deviationCalculator'
import { Box, IconButton, Stack, Tooltip } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface DeviationIndicatorAnswerProps {
  responseItem: ResponseItem
  previousAnswers: ResponseItem[]
}

const DeviationIndicatorAnswer: React.FC<DeviationIndicatorAnswerProps> = ({ responseItem, previousAnswers }) => {
  const { formatMessage } = useIntl()
  const unit = responseItem.requestItem?.unit?.symbol ?? ''
  const previousAnswersQuestion = previousAnswers.filter(
    p => p.requestItem.questionId === responseItem.requestItem.questionId,
  )
  const deviationResult = calculateDeviation(
    responseItem.answer,
    previousAnswersQuestion,
    responseItem.response.request.periodName,
    responseItem.response.request.questionnaireTemplateId,
  )

  const previousAnswersSummaries = getPreviousAnswerSummary(responseItem, previousAnswersQuestion)
  if (!deviationResult) return null
  return (
    <Tooltip
      title={
        <Stack direction="column">
          <Stack direction="row">
            <Box mr={4}>
              {deviationResult.previousAnswer === 0
                ? formatMessage({ id: 'deviation.notApplicable' })
                : deviationResult.deviation
                  ? formatMessage(
                      { id: 'deviation.emphasized' },
                      { deviation: deviationResult.deviation, b: (chunks: React.ReactNode) => <b>{chunks}</b> },
                    )
                  : formatMessage({ id: 'deviation.noDeviation' })}
            </Box>
            <DeviationIcon deviation={deviationResult.deviation} />
          </Stack>
          <Box key={responseItem.response.request.periodName}>
            {responseItem.response.request.periodName}: {responseItem.answer} {unit}
          </Box>
          {previousAnswersSummaries.length > 0 && (
            <Box key={previousAnswersSummaries[0]?.period}>
              {previousAnswersSummaries[0].period}:{' '}
              {previousAnswersSummaries[0].relevantAnswer
                ? `${previousAnswersSummaries[0].relevantAnswer} ${unit}`
                : formatMessage({ id: 'deviation.skipped' })}
            </Box>
          )}
        </Stack>
      }
    >
      <IconButton
        size="medium"
        sx={{
          borderRadius: '100px',
          '&:hover': {
            background: 'var(--action-hover, rgba(0, 0, 0, 0.04))',
          },
        }}
      >
        <DeviationIcon deviation={deviationResult.deviation} noMargin />
      </IconButton>
    </Tooltip>
  )
}

export default DeviationIndicatorAnswer
